import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { getAuthToken } from "../../utils/token";
import config from "../../config/config";
import Loader from "../loader/Loader";
import Select from "react-select";
const MachineSetupModal = ({
  show,
  handleClose,
  machineSetupId,
}) => {
  const initialFormData = {
    dateOfSetup: "",
    product_id: "",
    die_id: "",
    notes: "",
    checklist: {
      temperature: false,
      dieSize: false,
      pressure: false,
      time: false,
      volume: false,
    },
  };

  const [machinesetupwindow, setMachinesetupwindow] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show && machineSetupId) {
      setFormData(initialFormData);
      setErrors({});
      fetchMachinesetupwindow();
    }
  }, [show, machineSetupId]);

  const fetchMachinesetupwindow = async () => {
    setLoading(true);

    const token = getAuthToken();

    if (!token) {
      setLoading(false);
      toast.error("Please login first.");
      handleClose();
      return;
    }

    try {
      const res = await axios.get(
        `${config.API}/manager/machinesetupwindow/${machineSetupId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      if (res.status === 200 && res.data?.success) {
        const data = res.data.data;

        setMachinesetupwindow(data);

        // Existing product/die will be automatically selected.
        // If null, dropdown remains empty and all options are shown.
        setFormData((prev) => ({
          ...prev,
          product_id: data?.product_id ?? "",
          die_id: data?.die_id ?? "",
          dateOfSetup: data?.setup_date ?? "",
          notes: data?.notes ?? "",
        }));

        return;
      }

      setMachinesetupwindow(null);

      toast.error(
        res.data?.message ||
        "Failed to load machine setup data"
      );
    } catch (err) {
      console.error("Machine setup fetch error:", err);

      setMachinesetupwindow(null);

      if (err.response?.status === 401) {
        toast.error(
          "Session expired. Please login again."
        );

        localStorage.removeItem("user");
        localStorage.removeItem("token");
        localStorage.removeItem("access_token");
        localStorage.removeItem("jwt");
        sessionStorage.clear();

        setTimeout(() => {
          window.location.replace("/login");
        }, 1000);

        return;
      }

      toast.error(
        err.response?.data?.message ||
        "Failed to load machine setup data"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleChecklistChange = (e) => {
    const { name, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      checklist: {
        ...prev.checklist,
        [name]: checked,
      },
    }));
  };

  const handleSave = async () => {
    setErrors({});
    setLoading(true);

    const token = getAuthToken();

    if (!token) {
      setLoading(false);
      toast.error("Please login first.");
      return;
    }

    const payload = {
      machine_id: Number(
        machinesetupwindow?.machine_id
      ),

      product_id: formData.product_id
        ? Number(formData.product_id)
        : null,

      die_id: formData.die_id
        ? Number(formData.die_id)
        : null,

      machine_temperature:
        formData.checklist.temperature,

      die_size_enabled:
        formData.checklist.dieSize,

      injection_pressure:
        formData.checklist.pressure,

      injection_time:
        formData.checklist.time,

      injection_volume:
        formData.checklist.volume,

      setup_date: formData.dateOfSetup,

      notes: formData.notes,

      status: "saved",
    };

    try {
      const res = await axios.post(
        `${config.API}/manager/machinesetupwindow`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      if (res.data?.success === true) {
        toast.success(
          res.data.message ||
          "Saved successfully"
        );

        handleClose();
        return;
      }

      if (
        res.data?.success === false &&
        res.data?.message
      ) {
        setErrors(res.data.message);
        return;
      }
    } catch (err) {
      console.error(
        "Machine setup save error:",
        err
      );

      if (err.response?.status === 401) {
        toast.error(
          "Session expired. Please login again."
        );

        localStorage.removeItem("user");
        localStorage.removeItem("token");
        localStorage.removeItem("access_token");
        localStorage.removeItem("jwt");
        sessionStorage.clear();

        setTimeout(() => {
          window.location.replace("/login");
        }, 1000);

        return;
      }

      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      } else if (err.response?.data?.message) {
        setErrors(err.response.data.message);
      } else {
        toast.error("Server error");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading || !machinesetupwindow) {
    return (
      <Modal
        show={show}
        centered
        onHide={handleClose}
      >
        <Modal.Body className="text-center">
          <Loader />
          Loading...
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      className="font_family"
    >
      <Modal.Body>

        <h5 className="mb-3 fw-bold">
          Machine Setup Window
        </h5>

        <Form>

          {/* Machine */}
          <Form.Group className="mb-3">
            <Form.Label>Machine</Form.Label>

            <Form.Control
              type="text"
              value={
                machinesetupwindow?.machine || ""
              }
              readOnly
            />

            {errors.machine_id && (
              <small className="text-danger">
                {Array.isArray(errors.machine_id)
                  ? errors.machine_id[0]
                  : errors.machine_id}
              </small>
            )}
          </Form.Group>

          {/* Checklist */}
          <div className="border p-3 mb-3">

            {/* Machine Temperature */}
            <Form.Check
              label="Machine Temperature"
              name="temperature"
              checked={
                formData.checklist.temperature
              }
              onChange={handleChecklistChange}
            />

            {errors.machine_temperature && (
              <small className="text-danger d-block">
                {Array.isArray(
                  errors.machine_temperature
                )
                  ? errors.machine_temperature[0]
                  : errors.machine_temperature}
              </small>
            )}

            {/* Die Size */}
            <Form.Check
              label="Die Size"
              name="dieSize"
              checked={
                formData.checklist.dieSize
              }
              onChange={handleChecklistChange}
            />

            {errors.die_size_enabled && (
              <small className="text-danger d-block">
                {Array.isArray(
                  errors.die_size_enabled
                )
                  ? errors.die_size_enabled[0]
                  : errors.die_size_enabled}
              </small>
            )}

            {/* Injection Pressure */}
            <Form.Check
              label="Injection Pressure"
              name="pressure"
              checked={
                formData.checklist.pressure
              }
              onChange={handleChecklistChange}
            />

            {errors.injection_pressure && (
              <small className="text-danger d-block">
                {Array.isArray(
                  errors.injection_pressure
                )
                  ? errors.injection_pressure[0]
                  : errors.injection_pressure}
              </small>
            )}

            {/* Injection Time */}
            <Form.Check
              label="Injection Time"
              name="time"
              checked={
                formData.checklist.time
              }
              onChange={handleChecklistChange}
            />

            {errors.injection_time && (
              <small className="text-danger d-block">
                {Array.isArray(
                  errors.injection_time
                )
                  ? errors.injection_time[0]
                  : errors.injection_time}
              </small>
            )}

            {/* Injection Volume */}
            <Form.Check
              label="Injection Volume"
              name="volume"
              checked={
                formData.checklist.volume
              }
              onChange={handleChecklistChange}
            />

            {errors.injection_volume && (
              <small className="text-danger d-block">
                {Array.isArray(
                  errors.injection_volume
                )
                  ? errors.injection_volume[0]
                  : errors.injection_volume}
              </small>
            )}

          </div>

          {/* Product */}
          <Form.Group className="mb-3">
            <Form.Label>Product</Form.Label>

            <Form.Group className="mb-3">
              <Form.Label>Product</Form.Label>

              <Select
                name="product_id"
                options={(machinesetupwindow?.product || []).map((item) => ({
                  value: item.id,
                  label: item.name,
                }))}
                value={
                  (machinesetupwindow?.product || [])
                    .map((item) => ({
                      value: item.id,
                      label: item.name,
                    }))
                    .find(
                      (item) =>
                        String(item.value) ===
                        String(formData.product_id)
                    ) || null
                }
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    product_id: selected?.value || "",
                  }))
                }
                placeholder="Search / Select Product..."
                isSearchable
                isClearable
                menuPlacement="auto"
                maxMenuHeight={300}
                styles={{
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999,
                  }),
                  option: (base) => ({
                    ...base,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }),
                  singleValue: (base) => ({
                    ...base,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }),
                }}
              />

              {errors.product_id && (
                <small className="text-danger">
                  {Array.isArray(errors.product_id)
                    ? errors.product_id[0]
                    : errors.product_id}
                </small>
              )}
            </Form.Group>

            {errors.product_id && (
              <small className="text-danger">
                {Array.isArray(errors.product_id)
                  ? errors.product_id[0]
                  : errors.product_id}
              </small>
            )}
          </Form.Group>

          {/* Date + Die */}
          <Row className="mb-3">

            {/* Date */}
            <Col>
              <Form.Label>
                Date of Setup
              </Form.Label>

              <Form.Control
                type="date"
                name="dateOfSetup"
                value={formData.dateOfSetup}
                onChange={handleChange}
              />

              {errors.setup_date && (
                <small className="text-danger">
                  {Array.isArray(
                    errors.setup_date
                  )
                    ? errors.setup_date[0]
                    : errors.setup_date}
                </small>
              )}
            </Col>

            {/* Die */}
            <Col>
              <Form.Label>Die</Form.Label>

              <Form.Select
                name="die_id"
                value={formData.die_id}
                onChange={handleChange}
              >
                <option value="">
                  Select Die
                </option>

                {(machinesetupwindow?.die || []).map(
                  (item) => (
                    <option
                      key={item.id}
                      value={item.id}
                    >
                      {item.name}
                    </option>
                  )
                )}
              </Form.Select>

              {errors.die_id && (
                <small className="text-danger">
                  {Array.isArray(errors.die_id)
                    ? errors.die_id[0]
                    : errors.die_id}
                </small>
              )}
            </Col>

          </Row>

          {/* Notes */}
          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>

            <Form.Control
              as="textarea"
              rows={3}
              name="notes"
              value={formData.notes}
              onChange={handleChange}
            />

            {errors.notes && (
              <small className="text-danger">
                {Array.isArray(errors.notes)
                  ? errors.notes[0]
                  : errors.notes}
              </small>
            )}
          </Form.Group>

          {/* Buttons */}
          <div className="d-flex justify-content-end">

            <Button
              variant="secondary"
              onClick={handleClose}
              className="me-2"
              disabled={loading}
            >
              Cancel
            </Button>

            <Button
              variant="dark"
              onClick={handleSave}
              disabled={loading}
            >
              {loading
                ? "Saving..."
                : "Save"}
            </Button>

          </div>

        </Form>

      </Modal.Body>
    </Modal>
  );
};

export default MachineSetupModal;